// src/services/careTeamAPI.js
//
// The patient's care team — GET /api/patients/{patientId}/care-team.
//
// DEF-018 / DEF-019 / DEF-032. Before this endpoint existed, the Care Team page
// rendered the CONVERSATION list, so the care team was implicitly defined as
// "whoever I happen to have a chat thread with". A doctor's role came from the
// thread rather than from the person (DEF-018 showed a doctor as "Navigator"),
// and a doctor with a real appointment but no thread yet was invisible
// (DEF-019).
//
// The care team is the patient's assigned navigator plus the doctors they have a
// non-cancelled appointment with. It exists whether or not anyone has messaged.
// See PatientCareTeamController for the membership rules.
//
// Plain fetch + JWT, rather than the axios `api` instance — but, unlike
// chatMessagingAPI.js, deliberately WITHOUT its own 401/403 session-expiry
// handling.
//
// PatientContext.refreshCareTeam() (the only caller) already wraps this in a
// silent .catch(): "a failed fetch must not block the portal". This used to
// carry the same ad-hoc "401/403 → clear the token and hard-redirect to
// /login" handling chatMessagingAPI.js uses, which defeated that contract —
// it fired the redirect (a real navigation, not something a .catch() can
// undo) before the rejection ever reached PatientContext's silent catch.
// Reported as patients being bounced back to the login screen right after a
// successful sign-in (both password and OTP — refreshCareTeam runs for
// either the moment patientId is set), intermittently: it only happened on
// whatever fraction of sessions saw a transient 401/403 from THIS ONE
// endpoint, one call among several PatientContext fires on every login,
// while the login itself, and the JWT it produced, were both fine. The
// app's single source of truth for "the session is actually gone" is
// api.js's axios interceptor (debounced, shows a toast, used by every
// axios-backed call including patientAPI.get) — this endpoint has no
// business overriding that with its own harsher, unsynchronized copy.
// A genuinely expired/invalid token still ends the session: api.js's
// interceptor catches it on the very next axios-backed call (refreshProfile
// runs alongside this on every login).

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

function authHeaders() {
  const jwt = localStorage.getItem('previu_token');
  return {
    'Content-Type': 'application/json',
    ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
  };
}

async function handle(response) {
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.message || `Could not load your care team (${response.status})`);
  }
  return response.json();
}

export const careTeamAPI = {
  /**
   * @returns {Promise<Array<{
   *   id: string, name: string, role: 'DOCTOR'|'NAVIGATOR',
   *   specialty: ?string, conversationId: ?string, conversationType: string
   * }>>}
   *
   * conversationId is null when no thread has been started with that member yet.
   * That is the normal case for a newly-assigned doctor, not an error — the
   * caller creates the thread on first message.
   */
  list: (patientId) =>
    fetch(`${API_BASE}/patients/${patientId}/care-team`, { headers: authHeaders() })
      .then(handle),
};
